import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { DynamicSEOHead } from '@/components/DynamicSEOHead';
import { DynamicOrganizationStructuredData, DynamicWebSiteStructuredData } from '@/components/DynamicStructuredData';
import { ListingCard } from '@/components/ListingCard';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { CalendarIcon, Search, X, Globe } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { AdBanner } from '@/components/AdSense';
import { COUNTRIES } from '@/constants/countries';
import { useFavorites } from '@/hooks/useFavorites';
import { AuthSheet } from '@/components/AuthSheet';
import { NearbyEvents } from '@/components/NearbyEvents';
import { CategoryCarousel } from '@/components/CategoryCarousel';
import { LocationCarousel } from '@/components/LocationCarousel';

import { HomepageHero } from '@/components/HomepageHero';
import { User } from '@supabase/supabase-js';
interface Event {
  id: string;
  slug: string;
  title: string;
  date: string;
  time: string;
  background_image_url: string;
  target_date: string;
  address: string;
  city: string | null;
  country: string | null;
  category: string | null;
  description?: string;
  is_free: boolean;
  is_featured: boolean;
  featured_until: string | null;
}
const CATEGORIES = ['All', 'Car Meet', 'Race', 'Exhibition', 'Track Day', 'Rally', 'Drift'] as const;
const Index = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'All');
  const [selectedCountry, setSelectedCountry] = useState(searchParams.get('country') || 'All');
  const [showFreeOnly, setShowFreeOnly] = useState(searchParams.get('free') === 'true');
  const [user, setUser] = useState<User | null>(null);
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const handleAuthRequired = useCallback(() => {
    setIsAuthOpen(true);
  }, []);
  const {
    isEventFavorite,
    toggleEventFavorite
  } = useFavorites(user?.id, {
    onAuthRequired: handleAuthRequired
  });
  useEffect(() => {
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange((_, session) => {
      setUser(session?.user ?? null);
    });
    supabase.auth.getSession().then(({
      data: {
        session
      }
    }) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);
  useEffect(() => {
    fetchEvents();
  }, []);
  useEffect(() => {
    // Don't rewrite the URL while OAuth is completing.
    // Supabase needs the `?code=...` (or token fragment) to exchange and persist the session.
    const hasOAuthParams = searchParams.has('code') || searchParams.has('error') || searchParams.has('error_description') || searchParams.has('access_token') || window.location.hash.includes('access_token');
    if (hasOAuthParams) return;
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (selectedCategory !== 'All') params.set('category', selectedCategory);
    if (selectedCountry !== 'All') params.set('country', selectedCountry);
    if (showFreeOnly) params.set('free', 'true');
    setSearchParams(params, {
      replace: true
    });
  }, [searchQuery, selectedCategory, selectedCountry, showFreeOnly, setSearchParams, searchParams]);
  const fetchEvents = async () => {
    try {
      const {
        data,
        error
      } = await supabase.from('events').select('id, slug, title, date, time, background_image_url, target_date, address, city, country, category, description, is_free, is_featured, featured_until').eq('is_hidden', false).order('target_date', {
        ascending: true
      });
      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };
  const isEventLive = (event: Event) => {
    const now = new Date().getTime();
    const target = new Date(event.target_date).getTime();
    const oneHour = 1000 * 60 * 60;
    return now >= target && now <= target + oneHour;
  };
  const isCurrentlyFeatured = (event: Event) => {
    return event.is_featured && (!event.featured_until || new Date(event.featured_until) > new Date());
  };
  const filteredEvents = events.filter(event => {
    const now = new Date().getTime();
    const target = new Date(event.target_date).getTime();
    const oneHour = 1000 * 60 * 60;
    if (target < now - oneHour) return false;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      const matchesTitle = event.title.toLowerCase().includes(query);
      const matchesAddress = event.address.toLowerCase().includes(query);
      const matchesDescription = event.description?.toLowerCase().includes(query) || false;
      if (!matchesTitle && !matchesAddress && !matchesDescription) return false;
    }
    if (selectedCategory !== 'All' && event.category !== selectedCategory) return false;
    if (selectedCountry !== 'All' && event.country !== selectedCountry) return false;
    if (date) {
      const eventDate = new Date(event.target_date);
      if (eventDate.getFullYear() !== date.getFullYear() || eventDate.getMonth() !== date.getMonth() || eventDate.getDate() !== date.getDate()) return false;
    }

    // Free only filter
    if (showFreeOnly && !event.is_free) return false;
    return true;
  }).sort((a, b) => {
    // Featured events first
    const aFeatured = isCurrentlyFeatured(a);
    const bFeatured = isCurrentlyFeatured(b);
    if (aFeatured && !bFeatured) return -1;
    if (!aFeatured && bFeatured) return 1;
    // Then by date
    return new Date(a.target_date).getTime() - new Date(b.target_date).getTime();
  });
  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('All');
    setSelectedCountry('All');
    setDate(undefined);
    setShowFreeOnly(false);
  };
  const hasActiveFilters = searchQuery || selectedCategory !== 'All' || selectedCountry !== 'All' || date || showFreeOnly;

  // Get unique countries from events for the filter
  const availableCountries = [...new Set(events.map(e => e.country).filter(Boolean))] as string[];
  return <>
      <DynamicSEOHead 
        pagePath="/"
        fallbackTitle="MotoReveal | Discover Car Events & Motorsport Experiences Worldwide" 
        fallbackDescription="Discover car meets, track days, races, and motorsport experiences worldwide." 
        fallbackKeywords="car events, car meets, motorsport, racing, track day, car shows, automotive"
        url={`${window.location.origin}/`}
      />
      <DynamicOrganizationStructuredData />
      <DynamicWebSiteStructuredData />
      
      <div className="min-h-screen bg-background">
        <Navbar />
        
        <main className="pt-40 md:pt-48 pb-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {/* Hero Section - Title */}
            <div className="mb-10">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3">
                Discover Car & Motorbike Events
              </h1>
              <p className="text-muted-foreground text-lg">
                Find car meets, track days, races, and motorsport experiences near you
              </p>
            </div>

            {/* Featured Homepage Hero Carousel */}
            <HomepageHero listingType="event" className="mb-10" />

            {/* Nearby Events Section */}
            <NearbyEvents isFavorite={isEventFavorite} onFavoriteToggle={toggleEventFavorite} />

            {/* Browse Event Categories */}
            <CategoryCarousel type="event" className="mb-10" />

            {/* Browse by Location */}
            <LocationCarousel className="mb-10" />

            {/* All Events Header */}
            <h2 className="text-xl font-semibold mb-6">All Events</h2>

            {/* Search Bar */}
            <div className="mb-6">
              <div className="relative max-w-2xl">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <input type="text" placeholder="Search events by name or location..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-12 py-4 text-base border border-border rounded-full bg-background focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent placeholder:text-muted-foreground shadow-sm" />
                {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-4 top-1/2 -translate-y-1/2 p-1.5 hover:bg-muted rounded-full transition-colors">
                    <X className="h-4 w-4 text-muted-foreground" />
                  </button>}
              </div>
            </div>

            {/* Filter Pills */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              {CATEGORIES.map(category => <button key={category} onClick={() => setSelectedCategory(category)} className={cn("filter-pill", selectedCategory === category && "filter-pill-active")}>
                  {category}
                </button>)}

              {/* Date Picker */}
              <Popover>
                <PopoverTrigger asChild>
                  <button className={cn("filter-pill flex items-center gap-2", date && "filter-pill-active")}>
                    <CalendarIcon className="h-4 w-4" />
                    {date ? format(date, "MMM d") : "Any date"}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-background border border-border rounded-xl shadow-lg" align="start">
                  <Calendar mode="single" selected={date} onSelect={setDate} />
                </PopoverContent>
              </Popover>

              {/* Country Filter */}
              <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                <SelectTrigger className={cn("w-auto min-w-[140px] h-9 rounded-full border-border text-sm", selectedCountry !== 'All' && "bg-foreground text-background border-foreground")}>
                  <Globe className="h-4 w-4 mr-1" />
                  <SelectValue placeholder="Country" />
                </SelectTrigger>
                <SelectContent className="bg-background max-h-60">
                  <SelectItem value="All">All Countries</SelectItem>
                  {availableCountries.map(country => <SelectItem key={country} value={country}>{country}</SelectItem>)}
                </SelectContent>
              </Select>

              {/* Free Only Toggle */}
              <button onClick={() => setShowFreeOnly(!showFreeOnly)} className={cn("filter-pill flex items-center gap-2", showFreeOnly && "filter-pill-active")}>
                Free Only
              </button>

              {/* Clear Filters */}
              {hasActiveFilters && <button onClick={clearFilters} className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-4">
                  Clear all
                </button>}
            </div>

            {/* Results Count */}
            <div className="mb-4">
              <p className="text-sm text-muted-foreground">
                {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''} found
              </p>
            </div>

            {/* Event Grid - Desktop / Horizontal Scroll - Mobile */}
            {loading ? <>
                {/* Mobile skeleton */}
                <div className="flex gap-4 overflow-hidden md:hidden">
                  {[...Array(3)].map((_, i) => <div key={i} className="flex-shrink-0 w-[45%] animate-pulse">
                      <div className="aspect-square bg-muted rounded-xl mb-3" />
                      <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                      <div className="h-3 bg-muted rounded w-1/2" />
                    </div>)}
                </div>
                {/* Desktop skeleton */}
                <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {[...Array(8)].map((_, i) => <div key={i} className="animate-pulse">
                      <div className="aspect-square bg-muted rounded-xl mb-3" />
                      <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                      <div className="h-3 bg-muted rounded w-1/2" />
                    </div>)}
                </div>
              </> : filteredEvents.length === 0 ? <div className="text-center py-16">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-2">No events found</h3>
                <p className="text-muted-foreground mb-4">
                  Try adjusting your search or filters
                </p>
                <button onClick={clearFilters} className="text-primary hover:underline font-medium">
                  Clear all filters
                </button>
              </div> : <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-6">
                {filteredEvents.map((event, index) => (
                  <div 
                    key={event.id} 
                    className="animate-fade-in" 
                    style={{
                      animationDelay: `${index * 0.05}s`,
                      animationFillMode: 'both'
                    }}
                  >
                    <ListingCard 
                      id={event.id} 
                      slug={event.slug} 
                      title={event.title} 
                      subtitle={event.address} 
                      image={event.background_image_url} 
                      date={event.date} 
                      time={event.time} 
                      category={event.category} 
                      linkPrefix="/event" 
                      showFavorite={true} 
                      isFavorite={isEventFavorite(event.id)} 
                      onFavoriteToggle={toggleEventFavorite} 
                      isLive={isEventLive(event)} 
                      isFree={event.is_free} 
                      isFeatured={isCurrentlyFeatured(event)} 
                    />
                  </div>
                ))}
              </div>}

            {/* Ad Banner - between content and footer */}
            {filteredEvents.length > 0 && <AdBanner slot="3942848934" className="mt-12" />}
          </div>
        </main>


        <Footer />
      </div>
      
      <AuthSheet isOpen={isAuthOpen} onClose={() => setIsAuthOpen(false)} />
    </>;
};
export default Index;